// src/components/admin/Orders/OrdersList.tsx
import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { Link, useMatch } from "react-router-dom";
import { Table, Tag, Modal, Button } from "antd";
import styled from "styled-components";
import { withFirebase } from "../../dev/Firebase/Firebase";
import { OrderStatus } from "../../../constants/OrderStatusEnum";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";

const stringComparator = (s1: string, s2: string): number => (s1 > s2 ? 1 : s2 > s1 ? -1 : 0);

const columns = (path: string, showConfirm: (orderId: string, uid: string) => void) => [
  {
    title: "OLSLM Asset",
    dataIndex: "type",
    key: "type",
    sorter: (a: Order, b: Order) => stringComparator(a.type, b.type),
  },
  {
    title: "Rsrv. Amount",
    dataIndex: "reserveAmount",
    key: "reserveAmount",
    sorter: (a: Order, b: Order) => a.reserveAmount - b.reserveAmount,
  },
  {
    title: "Price",
    dataIndex: "price",
    key: "price",
    render: (text: number) => `$${text}`,
    sorter: (a: Order, b: Order) => a.price - b.price,
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (text: string, record: Order) =>
      record.status ? (
        record.status === OrderStatus.RESERVED ? (
          <Tag color="cyan">Reserved</Tag>
        ) : record.status === OrderStatus.PENDING ? (
          <Tag color="blue">Pending</Tag>
        ) : record.status === OrderStatus.DECLINED ? (
          <Tag color="red">Canceled</Tag>
        ) : (
          record.status === OrderStatus.COMPLETED && <Tag color="green">Paid</Tag>
        )
      ) : (
        <Tag color="red">No Status</Tag>
      ),
    sorter: (a: Order, b: Order) => stringComparator(a.status, b.status),
  },
  {
    title: "Created",
    dataIndex: "createdAt",
    key: "createdat",
    render: (text: number, record: Order) =>
      format(new Date(record.createdAt * 1000), "h:mm a MM/dd/yy"),
    sorter: (a: Order, b: Order) => a.createdAt - b.createdAt,
  },
  {
    title: "Action",
    key: "action",
    render: (text: string, record: Order) => (
      <span style={{ display: "flex", alignItems: "center" }}>
        <Link to={`${path}/${record.id}`} aria-label={`View details for order ${record.id}`}>
          <Button type="primary">Details</Button>
        </Link>
        <Button
          type="danger"
          style={{ marginLeft: "10px" }}
          onClick={() => showConfirm(record.id, record.uid)}
          aria-label={`Delete order ${record.id}`}
        >
          Delete
        </Button>
      </span>
    ),
  },
];

const StyledTable = styled(Table)`
  padding: 24px;
  background: linear-gradient(to bottom, #fff5e6, #fff);
  box-shadow: 0 4px 15px rgba(255, 204, 0, 0.2);
  border-radius: 8px;
  animation: fadeIn 1.5s ease-in;

  @media (max-width: 768px) {
    .ant-table {
      font-size: 14px;
    }
    .ant-btn {
      width: 100%;
    }
  }
`;

interface Order {
  id: string;
  key: string;
  type: string;
  reserveAmount: number;
  price: number;
  status: string;
  createdAt: number;
  uid: string;
}

interface OrdersListProps {
  firebase: any; // Replace with FirebaseContextType
}

const OrdersList: React.FC<OrdersListProps> = ({ firebase }) => {
  const match = useMatch("/admin/orders/*");
  const [orders, setOrders] = useState<Order[]>();

  const showConfirm = (orderId: string, uid: string) => {
    Modal.confirm({
      title: "Delete order",
      okText: "Delete",
      content: `Do you want to delete order #${orderId}`,
      async onOk() {
        try {
          await firebase.userOrder(uid, orderId).delete();
          toast.success("Order successfully deleted");
        } catch (error) {
          toast.error("Oops, something went wrong");
          console.log(error);
        }
      },
      onCancel() {},
    });
  };

  useEffect(
    () =>
      firebase
        .orders()
        .orderBy("createdAt", "desc")
        .onSnapshot(({ docs }: any) => {
          const ordersList = docs.map((doc: any) => ({
            id: doc.id,
            key: doc.id,
            ...doc.data(),
          }));
          setOrders(ordersList);
        }),
    []
  );

  return (
    <StyledTable
      columns={columns(match?.pathname ?? "/admin/orders", showConfirm)}
      dataSource={orders}
      loading={!orders}
      pagination={{ pageSize: 10 }}
      aria-label="Orders list table"
    />
  );
};

export default withFirebase(OrdersList);